import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { TrendingUp, TrendingDown, Activity, BarChart3, Search, RefreshCw, Zap, ArrowRight } from "lucide-react";
import { formatPrice } from "../lib/utils";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CRYPTO_LOGOS = {
  bitcoin: "https://assets.coingecko.com/coins/images/1/small/bitcoin.png",
  ethereum: "https://assets.coingecko.com/coins/images/279/small/ethereum.png",
  binancecoin: "https://assets.coingecko.com/coins/images/825/small/bnb-icon2_2x.png",
  solana: "https://assets.coingecko.com/coins/images/4128/small/solana.png",
  ripple: "https://assets.coingecko.com/coins/images/44/small/xrp-symbol-white-128.png",
  cardano: "https://assets.coingecko.com/coins/images/975/small/cardano.png",
  dogecoin: "https://assets.coingecko.com/coins/images/5/small/dogecoin.png",
  polygon: "https://assets.coingecko.com/coins/images/4713/small/matic-token-icon.png",
  polkadot: "https://assets.coingecko.com/coins/images/12171/small/polkadot.png",
  "avalanche-2": "https://assets.coingecko.com/coins/images/12559/small/Avalanche_Circle_RedWhite_Trans.png",
  chainlink: "https://assets.coingecko.com/coins/images/877/small/chainlink-new-logo.png",
  uniswap: "https://assets.coingecko.com/coins/images/12504/small/uniswap-uni.png",
  cosmos: "https://assets.coingecko.com/coins/images/1481/small/cosmos_hub.png",
  litecoin: "https://assets.coingecko.com/coins/images/2/small/litecoin.png",
  tron: "https://assets.coingecko.com/coins/images/1094/small/tron-logo.png",
};

const STOCK_LOGOS = {
  AAPL: "https://logo.clearbit.com/apple.com",
  GOOGL: "https://logo.clearbit.com/google.com",
  MSFT: "https://logo.clearbit.com/microsoft.com",
  AMZN: "https://logo.clearbit.com/amazon.com",
  TSLA: "https://logo.clearbit.com/tesla.com",
  NVDA: "https://logo.clearbit.com/nvidia.com",
  META: "https://logo.clearbit.com/meta.com",
  JPM: "https://logo.clearbit.com/jpmorganchase.com",
  V: "https://logo.clearbit.com/visa.com",
  JNJ: "https://logo.clearbit.com/jnj.com",
  WMT: "https://logo.clearbit.com/walmart.com",
  PG: "https://logo.clearbit.com/pg.com",
  MA: "https://logo.clearbit.com/mastercard.com",
  UNH: "https://logo.clearbit.com/unitedhealthgroup.com",
  HD: "https://logo.clearbit.com/homedepot.com",
  DIS: "https://logo.clearbit.com/disney.com",
  PYPL: "https://logo.clearbit.com/paypal.com",
  NFLX: "https://logo.clearbit.com/netflix.com",
  INTC: "https://logo.clearbit.com/intel.com",
  AMD: "https://logo.clearbit.com/amd.com",
};

export default function Dashboard() {
  const [cryptoPrices, setCryptoPrices] = useState([]);
  const [stockPrices, setStockPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchCrypto, setSearchCrypto] = useState("");
  const [searchStock, setSearchStock] = useState("");
  const [lastUpdate, setLastUpdate] = useState(new Date());

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [cryptoRes, stockRes] = await Promise.all([
          axios.get(`${API}/crypto/prices`).catch(() => ({ data: { prices: [] } })),
          axios.get(`${API}/stocks/prices`).catch(() => ({ data: { prices: [] } }))
        ]);
        
        if (cryptoRes.data.prices?.length > 0) setCryptoPrices(cryptoRes.data.prices);
        if (stockRes.data.prices?.length > 0) setStockPrices(stockRes.data.prices);
        setLastUpdate(new Date());
      } catch (e) {
        console.error("Error cargando precios:", e);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, []);

  const formatPriceLocal = (price) => {
    if (price >= 1000) return price.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (price >= 1) return price.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
    return price.toFixed(6).replace('.', ',');
  };

  const formatMarketCap = (cap) => {
    if (cap >= 1e12) return `${(cap / 1e12).toFixed(2).replace('.', ',')}T\u20AC`;
    if (cap >= 1e9) return `${(cap / 1e9).toFixed(2).replace('.', ',')}B\u20AC`;
    if (cap >= 1e6) return `${(cap / 1e6).toFixed(2).replace('.', ',')}M\u20AC`;
    return cap.toLocaleString('es-ES') + '\u20AC';
  };

  const filteredCrypto = cryptoPrices.filter(c => 
    c.name.toLowerCase().includes(searchCrypto.toLowerCase()) ||
    c.symbol.toLowerCase().includes(searchCrypto.toLowerCase())
  );

  const filteredStocks = stockPrices.filter(s => 
    s.name.toLowerCase().includes(searchStock.toLowerCase()) ||
    s.symbol.toLowerCase().includes(searchStock.toLowerCase())
  );

  const CryptoTable = ({ data }) => (
    <div className="overflow-x-auto">
      <table className="table-dark w-full">
        <thead>
          <tr>
            <th className="hidden md:table-cell w-12 text-center">#</th>
            <th className="text-left text-[10px] md:text-xs pl-3 md:pl-4">ACTIVO</th>
            <th className="text-left text-[10px] md:text-xs pl-4">PRECIO</th>
            <th className="text-left text-[10px] md:text-xs">24H</th>
            <th className="hidden md:table-cell text-right pr-4">CAP. MERCADO</th>
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => {
            const isPositive = item.price_change_percentage_24h >= 0;
            const logoUrl = CRYPTO_LOGOS[item.id] || CRYPTO_LOGOS[item.symbol?.toLowerCase()] || item.image;
            return (
              <tr key={item.id} data-testid={`crypto-row-${item.id}`} className="hover:bg-[#1E2329] transition-colors">
                <td className="hidden md:table-cell text-[#848E9C] font-mono text-center text-sm">{index + 1}</td>
                <td className="pl-3 md:pl-4">
                  <div className="flex items-center gap-2 md:gap-3">
                    {logoUrl ? (
                      <img 
                        src={logoUrl} 
                        alt={item.name} 
                        className="w-6 h-6 md:w-8 md:h-8 rounded-full bg-[#2B3139] flex-shrink-0"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-6 h-6 md:w-8 md:h-8 rounded-full bg-[#FCD535] flex items-center justify-center text-[#0B0E11] text-[10px] md:text-sm font-bold flex-shrink-0">
                        {item.symbol?.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="font-semibold text-[11px] md:text-sm text-[#EAECEF]">
                        <span className="md:hidden">{item.symbol?.toUpperCase()}</span>
                        <span className="hidden md:inline">{item.name}</span>
                      </p>
                      <p className="hidden md:block text-xs text-[#848E9C] uppercase">{item.symbol}</p>
                    </div>
                  </div>
                </td>
                <td className="text-left pl-4">
                  <span className="font-mono text-xs md:text-base font-bold text-[#EAECEF]">
                    {formatPriceLocal(item.current_price)}{'\u20AC'}
                  </span>
                </td>
                <td className="text-left">
                  <span className={`font-mono text-[10px] md:text-sm font-semibold ${isPositive ? 'text-[#0ECB81]' : 'text-[#F6465D]'}`}>
                    {isPositive ? '+' : ''}{item.price_change_percentage_24h?.toFixed(2).replace('.', ',')}%
                  </span>
                </td>
                <td className="hidden md:table-cell text-right font-mono text-sm text-[#848E9C] pr-4">
                  {formatMarketCap(item.market_cap)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const StockTable = ({ data }) => (
    <div className="overflow-x-auto">
      <table className="table-dark w-full">
        <thead>
          <tr>
            <th className="hidden md:table-cell w-12">#</th>
            <th className="text-left text-[10px] md:text-xs">Activo</th>
            <th className="text-right text-[10px] md:text-xs">Precio</th>
            <th className="text-right text-[10px] md:text-xs">24h</th>
            <th className="hidden md:table-cell text-right">Cap. Mercado</th>
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => {
            const price = item.price || item.current_price;
            const priceChange = item.price_change || item.change_percent || 0;
            const isPositive = priceChange >= 0;
            return (
              <tr key={item.symbol} data-testid={`stock-row-${item.symbol}`} className="hover:bg-[#1E2329] transition-colors">
                <td className="hidden md:table-cell text-[#848E9C] font-mono text-sm">{index + 1}</td>
                <td>
                  <div className="flex items-center gap-2 md:gap-3">
                    <img 
                      src={STOCK_LOGOS[item.symbol] || `https://via.placeholder.com/32?text=${item.symbol}`}
                      alt={item.name}
                      className="w-6 h-6 md:w-8 md:h-8 rounded-full bg-[#2B3139]"
                      onError={(e) => e.target.src = `https://via.placeholder.com/32?text=${item.symbol}`}
                    />
                    <div>
                      <p className="font-semibold text-[11px] md:text-sm text-[#EAECEF]">
                        <span className="md:hidden">{item.symbol}</span>
                        <span className="hidden md:inline">{item.name}</span>
                      </p>
                      <p className="hidden md:block text-xs text-[#848E9C]">{item.symbol}</p>
                    </div>
                  </div>
                </td>
                <td className="text-right">
                  <span className="font-mono text-xs md:text-base font-bold text-[#EAECEF]">
                    {formatPriceLocal(price)}{'\u20AC'}
                  </span>
                </td>
                <td className="text-right">
                  <span className={`font-mono text-[10px] md:text-sm font-semibold ${isPositive ? 'text-[#0ECB81]' : 'text-[#F6465D]'}`}>
                    {isPositive ? '+' : ''}{priceChange?.toFixed(2).replace('.', ',')}%
                  </span>
                </td>
                <td className="hidden md:table-cell text-right font-mono text-sm text-[#848E9C]">
                  {formatMarketCap(item.market_cap)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="loading">
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-[#0ECB81] border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-3 text-[#848E9C] text-sm">Cargando mercados...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-5" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-[#0ECB81] to-[#0ECB81]/50 rounded-xl flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-[#EAECEF]">Mercados</h1>
            <p className="text-[#848E9C] text-xs md:text-sm hidden md:block">Precios en tiempo real de criptomonedas y acciones</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 bg-[#0ECB81]/10 px-3 py-1.5 rounded-full">
            <span className="w-2 h-2 bg-[#0ECB81] rounded-full animate-pulse" />
            <span className="text-[#0ECB81] font-semibold text-xs">EN VIVO</span>
          </div>
          <div className="hidden md:flex items-center gap-2 text-[#848E9C] text-xs">
            <RefreshCw className="w-3.5 h-3.5" />
            <span>{lastUpdate.toLocaleTimeString()}</span>
          </div>
        </div>
      </div>

      {/* CTA Banner */}
      <Card className="card-dark border-[#FCD535]/20 overflow-hidden">
        <div className="bg-gradient-to-r from-[#FCD535]/5 to-[#0ECB81]/5 p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <Zap className="w-8 h-8 text-[#FCD535] flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-[#EAECEF] font-semibold text-sm">No solo mires los precios, gana con ellos</p>
                <p className="text-[#848E9C] text-xs hidden sm:block">Activa Copy Trading y genera ganancias automaticamente</p>
              </div>
            </div>
            <Link to="/copy-trading" className="flex-shrink-0">
              <Button className="btn-primary text-xs sm:text-sm px-4 py-2" data-testid="cta-copy-trading">
                <span className="hidden sm:inline">Activar Copy Trading</span>
                <span className="sm:hidden">Invertir</span>
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </Card>

      {/* Top Cryptos Strip */}
      <div className="grid grid-cols-4 gap-1.5 md:gap-3">
        {[
          { id: 'bitcoin', name: 'BTC', color: '#F7931A' },
          { id: 'ethereum', name: 'ETH', color: '#627EEA' },
          { id: 'binancecoin', name: 'BNB', color: '#F0B90B' },
          { id: 'solana', name: 'SOL', color: '#9945FF' },
        ].map(coin => {
          const data = cryptoPrices.find(c => c.id === coin.id);
          const change = data?.price_change_percentage_24h || 0;
          return (
            <Card key={coin.id} className="card-dark" style={{ borderTopColor: coin.color, borderTopWidth: '2px' }}>
              <CardContent className="p-2 md:p-3">
                <div className="flex flex-col md:flex-row items-center md:gap-2.5 text-center md:text-left">
                  <img src={CRYPTO_LOGOS[coin.id]} alt={coin.name} className="w-6 h-6 md:w-7 md:h-7 mb-1 md:mb-0" />
                  <div>
                    <p className="text-[9px] md:text-xs text-[#848E9C]">{coin.name}</p>
                    <p className="text-xs md:text-base font-bold font-mono text-[#EAECEF]">
                      {data?.current_price ? `${(data.current_price >= 1000 ? Math.round(data.current_price / 1000) + 'K' : data.current_price.toLocaleString('es-ES', {maximumFractionDigits: 0}))}` : '---'}
                      <span className="hidden md:inline">{'\u20AC'}</span>
                    </p>
                    <p className={`text-[8px] md:text-xs font-mono ${change >= 0 ? 'text-[#0ECB81]' : 'text-[#F6465D]'}`}>
                      {change >= 0 ? '+' : ''}{change.toFixed(1)}%
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="crypto" className="w-full">
        <TabsList className="bg-[#181A20] border border-[#2B3139] w-full md:w-auto">
          <TabsTrigger 
            value="crypto" 
            className="data-[state=active]:bg-[#0ECB81] data-[state=active]:text-white font-semibold text-xs md:text-sm flex-1 md:flex-none"
            data-testid="tab-crypto"
          >
            Cripto ({cryptoPrices.length})
          </TabsTrigger>
          <TabsTrigger 
            value="stocks" 
            className="data-[state=active]:bg-[#0ECB81] data-[state=active]:text-white font-semibold text-xs md:text-sm flex-1 md:flex-none"
            data-testid="tab-stocks"
          >
            Acciones ({stockPrices.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="crypto" className="mt-3">
          <Card className="card-dark">
            <CardHeader className="border-b border-[#2B3139] py-3 px-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <CardTitle className="text-[#EAECEF] text-base flex items-center gap-2">
                  <Activity className="w-4 h-4 text-[#0ECB81]" />
                  Criptomonedas
                </CardTitle>
                <div className="relative w-full sm:w-56">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#848E9C]" />
                  <Input 
                    placeholder="Buscar..."
                    value={searchCrypto}
                    onChange={(e) => setSearchCrypto(e.target.value)}
                    className="input-dark pl-9 h-9 text-sm"
                    data-testid="search-crypto"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <CryptoTable data={filteredCrypto} />
              {filteredCrypto.length === 0 && (
                <div className="p-8 text-center text-[#848E9C] text-sm">No se encontraron criptomonedas</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="stocks" className="mt-3">
          <Card className="card-dark">
            <CardHeader className="border-b border-[#2B3139] py-3 px-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <CardTitle className="text-[#EAECEF] text-base flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-[#0ECB81]" />
                  Acciones
                </CardTitle>
                <div className="relative w-full sm:w-56">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#848E9C]" />
                  <Input 
                    placeholder="Buscar..."
                    value={searchStock}
                    onChange={(e) => setSearchStock(e.target.value)}
                    className="input-dark pl-9 h-9 text-sm"
                    data-testid="search-stock"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <StockTable data={filteredStocks} />
              {filteredStocks.length === 0 && (
                <div className="p-8 text-center text-[#848E9C] text-sm">No se encontraron acciones</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
